/**
 * Formatters and small helpers for the My Accountant feature.
 * Pure functions, no React, no DOM access, no side effects.
 */

import type { InviteStatus, InviteDraft, InviteDraftErrors } from "./myAccountantTypes";

/** Days a pending invite stays valid. */
export const INVITE_EXPIRY_DAYS = 30;

/**
 * Long form: "October 22, 2026 at 4:12 PM"
 * Used for Invited and Expires lines on the pending card.
 */
export function formatDateLong(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Date only: "October 22, 2026"
 * Used for Added on the connected card.
 */
export function formatDateShort(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Date plus day count for the Expires line, e.g.
 * "November 21, 2026 (30 days)" when the invite was just sent,
 * "November 1, 2026 (10 days left)" partway through, or
 * "November 1, 2026 (expired)" once past.
 */
export function formatExpiryLabel(
  expiresAt: string,
  now: Date = new Date(),
): string {
  const expiry = new Date(expiresAt);
  if (isNaN(expiry.getTime())) return "";
  const base = formatDateShort(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return `${base} (expired)`;
  }
  if (diffDays === 0) {
    return `${base} (expires today)`;
  }
  if (diffDays === INVITE_EXPIRY_DAYS) {
    return `${base} (${INVITE_EXPIRY_DAYS} days)`;
  }
  if (diffDays === 1) {
    return `${base} (1 day left)`;
  }
  return `${base} (${diffDays} days left)`;
}

/**
 * Relative time: "2 hours ago", "yesterday", "3 days ago", "2 weeks ago".
 * Used for the Last signed in line on the connected card.
 */
export function formatRelativeTime(
  dateStr: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const diffSec = Math.floor((now.getTime() - d.getTime()) / 1000);

  if (diffSec < 60) return "just now";
  if (diffSec < 3600) {
    const mins = Math.floor(diffSec / 60);
    return mins === 1 ? "1 minute ago" : `${mins} minutes ago`;
  }
  if (diffSec < 86400) {
    const hrs = Math.floor(diffSec / 3600);
    return hrs === 1 ? "1 hour ago" : `${hrs} hours ago`;
  }
  if (diffSec < 172800) return "yesterday";
  if (diffSec < 604800) {
    const days = Math.floor(diffSec / 86400);
    return `${days} days ago`;
  }
  if (diffSec < 2592000) {
    const weeks = Math.floor(diffSec / 604800);
    return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
  }
  // Fall back to the date for anything older than ~30 days.
  return formatDateShort(dateStr);
}

/**
 * Compute the expiry timestamp from an invited-at timestamp.
 * Returns an ISO 8601 string.
 */
export function computeExpiryDate(invitedAt: string | Date): string {
  const base =
    typeof invitedAt === "string" ? new Date(invitedAt) : invitedAt;
  const expiry = new Date(base.getTime());
  expiry.setDate(expiry.getDate() + INVITE_EXPIRY_DAYS);
  return expiry.toISOString();
}

/**
 * Is the invite past its expiry? Pure check against now.
 */
export function isInviteExpired(
  expiresAt: string,
  now: Date = new Date(),
): boolean {
  const expiry = new Date(expiresAt);
  if (isNaN(expiry.getTime())) return false;
  return expiry.getTime() < now.getTime();
}

/**
 * Initials for the avatar. "Sarah Thompson" -> "ST".
 * Falls back to the first two letters of the email local part if
 * no name provided. "sarah@thompsonaccounting.ca" -> "SA".
 */
export function getInitials(name: string | undefined, email: string): string {
  const source = (name && name.trim()) || "";
  if (source) {
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  const local = (email || "").split("@")[0] || "";
  return local.slice(0, 2).toUpperCase() || "?";
}

/**
 * Display label for a status. Short form for the status pill.
 */
export function getStatusLabel(status: InviteStatus): string {
  switch (status) {
    case "pending":
      return "Invite pending";
    case "active":
      return "Active";
    case "expired":
      return "Invite expired";
    case "none":
    default:
      return "";
  }
}

/**
 * Basic RFC-5322-ish email validation. Not perfect; the server is
 * the source of truth. Good enough to catch obvious typos in the
 * invite drawer before submission.
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== "string") return false;
  const trimmed = email.trim();
  // Simple shape: something@something.tld, no spaces, at least one dot after @.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

/**
 * Validate the invite draft before submission.
 * Returns a map of field errors; empty object means draft is valid.
 */
export function validateInviteDraft(draft: InviteDraft): InviteDraftErrors {
  const errors: InviteDraftErrors = {};
  if (!draft.email || !draft.email.trim()) {
    errors.email = "Email is required.";
  } else if (!isValidEmail(draft.email)) {
    errors.email = "Enter a valid email address.";
  }
  // Name and firmName are optional in Phase A; add length cap only.
  if (draft.name && draft.name.length > 120) {
    errors.name = "Name is too long (max 120 characters).";
  }
  if (draft.firmName && draft.firmName.length > 120) {
    errors.firmName = "Firm name is too long (max 120 characters).";
  }
  return errors;
}

/**
 * True when the draft has no validation errors.
 */
export function isDraftValid(draft: InviteDraft): boolean {
  return Object.keys(validateInviteDraft(draft)).length === 0;
}

/**
 * Create an empty invite draft (pre-filled with blanks).
 */
export function createEmptyInviteDraft(): InviteDraft {
  return { email: "", name: "", firmName: "" };
}